// =======================
// Enums & Constants
// =======================
import { ThresholdVariant } from '@/modules/shared/DataLayers/types';

export type LevelName = 'healthy' | 'monitor' | 'alarm' | 'critical';

export enum DataLayer {
  OperatorProficiency = 'operator_proficiency',
  SensorFusion = 'sensor_fusion',
  Spectrum = 'spectrum',
  BeltMisalignment = 'beltMisalignment',
  RockSize = 'rockSize',
}

export enum AssetCategory {
  Mobile = 'mobile',
  Fixed = 'fixed',
}

// Configuration flags
export const API_CONFIG = {
  enableMockCustomLines: false, // Set to false to disable mock vertical lines
};

// =======================
// Types
// =======================

export type SearchParams = {
  site_id: string;
  asset_id: string;
  asset_name?: string;
  asset_category: string;
  component_id?: string | null;
  asset_supplier?: string | null;
  asset_model?: string | null;
  from_date?: string | null;
  to_date?: string | null;
  data_layer?: string;
  full_pull?: boolean;
  snapshot_timestamp?: string | string[] | null; // For spectral data timestamp selection
  // Operator proficiency specific params
  months_range?: number;
  asset_type?: string | string[];
};

export type ThresholdConfig = {
  id: string;
  x: number;
  label?: string;
  color?: string;
  type?: string;
};

export type LegendItem = {
  id: string;
  displayName: string;
  componentID: string;
  componentName?: string;
  coordination?: unknown;
  enabled: boolean;
  ui_threshold?: number | ThresholdConfig[] | null;
  unit?: string | null;
  featureDbName?: string;
};

export type Feature = {
  id: string;
  label?: string;
  featureDbName: string;
  unit: string;
  isDefault?: boolean;
};

export type ExpandableLegendItem = {
  id: string;
  value: string;
  label: string;
  enabled: boolean;
  source: 'system' | 'user';
  color: string;
  deletable: boolean;
  createdAt: string;
  markerSource?: string;
  markerId?: string;
};

export type ExpandableLegendConfig = {
  enabled: boolean;
  label: string;
  items: ExpandableLegendItem[];
  linkedSources?: Array<{
    sourceKey: string;
    shareUserMarkers: boolean;
    bidirectional: boolean;
    drawVerticalLines?: boolean;
  }>;
  defaultSelection: string[];
  multiSelect: boolean;
  defaultColor: string;
};

export type GraphDefinition = {
  id: string;
  title: string;
  featureDbName?: string;
  unit: string | null;
  splitToggle?: boolean;
  splitDefault?: boolean;
  splitCol?: number;
  features?: Feature[];
  legend: LegendItem[];
  enabled: boolean;
  colors?: string[];
  expandableLegend?: ExpandableLegendConfig;
  shapes?: Shapes;
  levels?: ShapeLevels;
  thresholdVariant?: ThresholdVariant; // 'flat' | 'bands-fill' | 'bands-gradient' (default: 'bands-fill')
};

type ShapeRange = {
  start: number | null;
  end: number | null;
};

type ShapeLevels = Record<LevelName, ShapeRange>;

export type Shapes = {
  levels: ShapeLevels;
  isMirrored: boolean;
  shouldCalculate?: boolean;
};
